import React, { useState, useEffect } from 'react';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface Permission {
  _id: string;
  metadata: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    permStr: string;
  };
}

interface PermissionManagementProps {
  skipLayout?: boolean;
}

const PermissionManagement: React.FC<PermissionManagementProps> = ({ skipLayout = false }) => {
  const { hasPermission } = useAuth();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [permToDelete, setPermToDelete] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    permStr: '',
  });
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ name?: string; permStr?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const { isSystemAdmin } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    if (isInitialLoad) return; // Skip debounce on initial load

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, isInitialLoad]);

  // Fetch permissions when debounced search term changes or on initial load
  useEffect(() => {
    if (!isInitialLoad) {
      setCurrentPage(1); // Reset to first page when search changes
    }
    fetchPermissions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  // Mark initial load as complete after first fetch
  useEffect(() => {
    if (isInitialLoad && !loading) {
      setIsInitialLoad(false);
    }
  }, [loading, isInitialLoad]);

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Refetch when debounced search changes
  useEffect(() => {
    // Avoid refetch before initial load if needed (optional)
    fetchPermissions(1, pageSize, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  const fetchPermissions = async (page: number, size: number = pageSize, search: string = debouncedSearchTerm) => {
    try {
      setLoading(true);
      const params: any = { page, pageSize: size };
      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }
      
      const response = await api.get('/permissions', {
        params: { page, pageSize: size, ...(search && { search: search.trim() }) },
      });
      const data = response.data.data || [];
      const pagination = response.data.pagination;

      setPermissions(data);
      if (pagination) {
        setCurrentPage(pagination.currentPage || page);
        setTotalItems(pagination.totalItems || data.length);
        setHasNextPage(!!pagination.hasNextPage);
        setHasPreviousPage(!!pagination.hasPreviousPage);
      } else {
        // Fallback if pagination is missing
        setCurrentPage(1);
      setTotalItems(data.length);
        setHasNextPage(false);
        setHasPreviousPage(false);
      }
    } catch (err: any) {
      setError('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (permission?: Permission) => {
    if (permission) {
      setEditingPermission(permission);
      setFormData({
        name: permission.metadata.name,
        permStr: permission.spec.permStr,
      });
    } else {
      setEditingPermission(null);
      setFormData({ name: '', permStr: '' });
    }
    setShowModal(true);
    setError('');
    setValidationErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setValidationErrors({});
    setSubmitting(true);

    const newValidationErrors: { name?: string; permStr?: string } = {};
    if (!formData.name.trim()) {
      newValidationErrors.name = 'Permission name is required';
    }
    if (!formData.permStr.trim()) {
      newValidationErrors.permStr = 'Permission string is required';
    }

    if (Object.keys(newValidationErrors).length > 0) {
      setValidationErrors(newValidationErrors);
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    try {
      const data = {
        metadata: { name: formData.name },
        spec: { permStr: formData.permStr },
      };

      if (editingPermission) {
        await api.put(`/permissions/${editingPermission._id}`, data);
      } else {
        await api.post('/permissions', data);
      }

      setShowModal(false);
      fetchPermissions(currentPage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    setPermToDelete({ id, name });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!permToDelete) return;

    try {
      await api.delete(`/permissions/${permToDelete.id}`);
      const targetPage = currentPage > 1 && permissions.length === 1 ? currentPage - 1 : currentPage;
      fetchPermissions(targetPage);
      setShowDeleteConfirm(false);
      setPermToDelete(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to delete permission. Unknown error occurred.');
      setShowDeleteConfirm(false);
      setPermToDelete(null);
      setShowErrorModal(true);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    fetchPermissions(currentPage, pageSize);
  };

  const content = (
    <>
      {/* <div className="page-header">
        <h2>Permission Management</h2>
        <p>
          {isSystemAdmin
            ? 'Manage system permissions'
            : 'View available permissions (read-only)'}
        </p>
      </div> */}

      {!isSystemAdmin && (
        <div className="info-box" style={{ marginBottom: '20px', padding: '12px 16px' }}>
          ℹ️ <strong>Note:</strong> Tenant admins can view permissions but cannot create, edit, or
          delete them. Use these permissions when creating roles.
        </div>
      )}

      <div className="card">
        <div className="card-header">
          {/* <h3 className="card-title">Permissions</h3> */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <input
              type="text"
              placeholder="Search by permission name or string..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
              }}
            />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {isSystemAdmin && hasPermission('permission.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Permission
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading permissions...</div>
        ) : permissions.length === 0 ? (
          <div className="empty-state">
            <h3>
              {debouncedSearchTerm ? 'No permissions found' : 'No permissions yet'}
            </h3>
            {debouncedSearchTerm ? (
              <p>Try adjusting your search terms</p>
            ) : (
              isSystemAdmin && <p>Create your first permission to get started</p>
            )}
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 520px)', overflow: 'auto' }}>
              <table className="bookings-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th>Name</th>
                    <th>Permission String</th>
                    <th>Created</th>
                    {isSystemAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((perm) => (
                    <tr key={perm._id}>
                      <td>{perm.metadata.name}</td>
                      <td>
                        <code>
                          {perm.spec.permStr}
                        </code>
                      </td>
                      <td>{formatDateDDMMYYYY(perm.metadata.createdAt)}</td>
                      {isSystemAdmin && (
                        <td>
                          <div className="action-buttons">
                            {hasPermission('permission.update') && (
                              <button className="btn-icon btn-edit" onClick={() => handleOpenModal(perm)}>
                                Edit
                              </button>
                            )}
                            {hasPermission('permission.delete') && (
                              <button className="btn-icon btn-delete" onClick={() => handleDelete(perm._id, perm.metadata.name)}>
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

              <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = Number(e.target.value);
                      setPageSize(newSize);
                      fetchPermissions(1, newSize);
                    }}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100, 500].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        totalItems
                      )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && fetchPermissions(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && fetchPermissions(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && isSystemAdmin && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {editingPermission ? 'Edit Permission' : 'Create New Permission'}
              </h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label>
                    Permission Name
                    <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={validationErrors.name ? 'input-error' : ''}
                    placeholder="e.g., Read Patients"
                  />
                  {validationErrors.name && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.name}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Permission String
                    <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.permStr}
                    onChange={(e) => setFormData({ ...formData, permStr: e.target.value })}
                    className={validationErrors.permStr ? 'input-error' : ''}
                    placeholder="resource.operation (e.g., patient.read)"
                  />
                  {validationErrors.permStr && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.permStr}
                    </small>
                  )}
                  <small className="helper-text" style={{ fontSize: '14px' }}>
                    Format: resource.operation (e.g., patient.read, user.create)
                  </small>
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingPermission ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    `${editingPermission ? 'Update' : 'Create'} Permission`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Permission"
        message={`Are you sure you want to delete the permission "${permToDelete?.name}"? This permission may be used in roles. This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setPermToDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="danger"
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
      />
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default PermissionManagement;

